/**
 * Image preload helpers.
 *
 * Two independent strategies, both driven from React so they play nice with
 * SPA route changes:
 *
 *   1. Critical preload (`<link rel="preload" as="image">`)
 *      -----------------------------------------------------------------
 *      Injected via react-helmet-async. The browser sees these hints during
 *      HTML parse (or as soon as React commits on soft-nav) and fetches the
 *      image with high priority BEFORE React mounts the component that
 *      actually paints it. Use this for above-the-fold assets whose URLs
 *      you know at page-composition time — hero background, header logo.
 *
 *   2. Background prefetch (`new Image().src = url`)
 *      -----------------------------------------------------------------
 *      Warms the HTTP cache silently after the current page has settled.
 *      Deferred via `requestIdleCallback` so it never competes with the
 *      current route's own fetches. Use this for images the user is *likely*
 *      to see next (e.g. queue the /properties card thumbnails while sitting
 *      on the home page). Dedupes globally so navigating back and forth
 *      doesn't fire redundant requests.
 *
 * The hooks are intentionally tiny — no cache invalidation, no timeout
 * plumbing. Both strategies gracefully no-op if the URL list is empty or if
 * running in a non-browser environment (SSR / tests).
 */
import { useEffect, useMemo } from "react";

// Module-scoped set so a URL is only prefetched once per browser session,
// even if multiple components ask for it.
const _prefetched = new Set();

// Track the <link rel="preload"> nodes we've added to <head> so we can
// tear them down (or reuse them) on route change instead of leaking one
// per navigation.
const _criticalLinks = new Map(); // url -> HTMLLinkElement

function _safeUrls(input) {
  if (!input) return [];
  const arr = Array.isArray(input) ? input : [input];
  return arr
    .map((u) => (typeof u === "string" ? u.trim() : ""))
    .filter((u) => !!u && !u.startsWith("data:")); // data URIs are already inline
}

/**
 * Inject `<link rel="preload" as="image">` for each URL into <head>.
 *
 * We manage the tags via direct DOM instead of react-helmet-async because
 * Helmet defers head updates until after commit + a microtask flush — long
 * enough for the browser to have already started the <img> fetch at normal
 * priority. Direct injection means the hint lands the instant we know the
 * URL, so subsequent <img> requests reuse the in-flight preloaded response.
 *
 *   useCriticalImagePreload([heroBg, logoUrl]);
 */
export function useCriticalImagePreload(urls) {
  // Stable primitive dep so we don't churn on every render.
  const key = useMemo(
    () => _safeUrls(urls).join("|"),
    [Array.isArray(urls) ? urls.join("|") : urls],
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    const list = key ? key.split("|") : [];
    const wanted = new Set(list);

    // Add links for URLs we haven't seen yet.
    list.forEach((url) => {
      if (_criticalLinks.has(url)) return;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = url;
      // `fetchpriority=high` bumps the request ahead of normal <img>s.
      link.setAttribute("fetchpriority", "high");
      link.dataset.imagePreload = "1";
      document.head.appendChild(link);
      _criticalLinks.set(url, link);
    });

    // Drop links that this page no longer needs. We do NOT remove links
    // that a *different* page still wants — the map is module-scoped and
    // shared. To keep things simple we only prune on unmount (below).
    return () => {
      wanted.forEach((url) => {
        const node = _criticalLinks.get(url);
        if (node && node.parentNode) node.parentNode.removeChild(node);
        _criticalLinks.delete(url);
      });
    };
  }, [key]);
}

/**
 * Silently fetch images once the current tick is idle.
 *
 *   useBackgroundImagePreload(nextRouteThumbUrls);
 *
 * Fires nothing during SSR. Reuses `requestIdleCallback` when available and
 * falls back to `setTimeout(…, 200)` so slower devices still get a chance
 * to finish their own critical work first.
 */
export function useBackgroundImagePreload(urls) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const list = _safeUrls(urls).filter((u) => !_prefetched.has(u));
    if (!list.length) return;

    let cancelled = false;
    const schedule = window.requestIdleCallback
      ? (cb) => window.requestIdleCallback(cb, { timeout: 2000 })
      : (cb) => window.setTimeout(cb, 200);
    const cancel = window.cancelIdleCallback || window.clearTimeout;

    const handle = schedule(() => {
      if (cancelled) return;
      list.forEach((url) => {
        if (_prefetched.has(url)) return;
        _prefetched.add(url);
        // Detached Image() — result lands in HTTP cache, nothing to render.
        // No error handler: a failed prefetch is invisible to the user; the
        // real <img> will surface the failure normally when it mounts.
        const img = new Image();
        img.decoding = "async";
        img.src = url;
      });
    });

    return () => {
      cancelled = true;
      try { cancel(handle); } catch { /* handle already ran */ }
    };
  // Stringify to give useEffect a stable primitive dep even when the caller
  // passes a fresh array each render.
  }, [Array.isArray(urls) ? urls.join("|") : urls]);
}
